import React, { useContext, useEffect, useRef, useState } from 'react'
import { Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddNewValue from './AddNewValue';
import QuickAddNewValues from './QuickAddNewValues';
import { treeActions } from "../../../../../redux/slices/treeSlice";
import { useDispatch, useSelector } from 'react-redux';
import { eachDeep } from "deepdash-es/standalone";
import { cloneDeep } from 'lodash';
import { getSelectedNodes } from '../../../../RightContextMenu/ContextActions/CopyAndPaste';
import ErrorModal from '../ErrorModal';
import { getRangeValueAsArray, getRangeValueAsString } from '../../../../../utils/rangeValueDestructure';
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import { api } from '../../../../../redux/api';
import { getParentNode } from '../../FieldPropertyForm';

const MODALS = {
	BTN_NEW_VALUE: 'BTN_NEW_VALUE',
	BTN_NEW_VALUES_QUICK_ADD: 'BTN_NEW_VALUES_QUICK_ADD',
	BTN_MODIFY_VALUE: 'BTN_MODIFY_VALUE'
}

const CONTAINS_TYPE = {
	DATE_CONTAINS: 'DATE_CONTAINS',
	CONTAINS: 'CONTAINS'
}

function Contains({ action }) {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const parentScreen = useContext(ParentScreenCtx);
	const { rightTree } = useSelector((state) => state.treeData[parentScreen]);
	const queryParams = useSelector((state) => state.queryParams);
	const templateHeader = rightTree?.tree?.templateHeader;
	const currentTab = rightTree.currentTab;
	const selectedNode = rightTree.tree[currentTab].selectedNode;
	const translatedData = rightTree.tree[currentTab].translatedData;
	const [modal, setModal] = useState(false);
	const [errorModal, setErrorModal] = useState(false);
	const [deleteModal, setDeleteModal] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [rows, setRows] = useState([]);
	const [selectRow, setSelectRow] = useState();
	const [selectedValues, setSelectedValues] = useState([]);
	const [node, setNode] = useState();
	const tableRef = useRef();
	const lastRowRef = useRef();
	const handleModals = (val) => {
		setModal(val);
	};

	const handleMulitpleSelection = (key, row) => {
		let kb = {
			ctrl: key.nativeEvent ? key.nativeEvent.ctrlKey : false,
			metaKey: key.nativeEvent ? key.nativeEvent.metaKey : false
		}
		if (kb.ctrl || kb.metaKey) {
			if (!selectedValues.includes(row.VALUE)) {
				const arr = [...selectedValues]
				setSelectedValues([...arr, row.VALUE])
			} else {
				const values = selectedValues?.filter(val => val !== row.VALUE);
				setSelectedValues(values);
			}
		}
		else if (!kb.ctrl || !kb.metaKey) {
			if (selectedValues.length > 1 && selectedValues.includes(row.VALUE)) {
				setSelectedValues([row.VALUE]);
				setSelectRow(row);
			} else if (selectedValues.length === 1 && selectedValues.includes(row.VALUE)) {
				setSelectedValues([]);
				setSelectRow({});
			} else {
				setSelectedValues([row.VALUE]);
				setSelectRow(row);
			}
		}
	}

	const errorData = (rows, val) => {
		let isError = false;
		let getRangeValue;

		for (const { VALUE } of rows) {
			if (VALUE?.toLowerCase() === val?.VALUE?.toLowerCase()) {
				isError = true;
				break;
			}
		}

		if (isError) {
			setErrorModal(true);
			setErrorMessage(t('BOX_VALUE_ALREADY_EXISTS'));
		} else {
			const newRows = [...rows, val];
			setRows(newRows);
			getRangeValue = getRangeValueAsString(newRows);
		}
		return getRangeValue;
	}

	const handleInput = (val) => {
		if (val.VALUE !== '') {
			// Assign ID to new value
			const nextId = rows.length > 0 ? Math.max(...rows.map(row => row.ID || 0)) + 1 : 0;
			const newVal = { ...val, ID: nextId };
			
			if (action === CONTAINS_TYPE.CONTAINS) {
				const rangeValue = errorData(rows, newVal);
				if (selectedNode?.CODE_LIST_DE_ID && rangeValue) {
					let formattedCode = `${val.VALUE}%1F${val.DESCRIPTION.replace(' ', '+')}%1E`;
					const newCodeListAdd = node.hasOwnProperty('CODELIST_ADD')
                    ? { ...node.CODELIST_ADD, CODES: node.CODELIST_ADD.CODES + formattedCode }
                    : { CODES: formattedCode };

                    node.CODELIST_ADD = newCodeListAdd;
					setModal(null);
				}
				else if (node?.RANGE_VALUES) {
					if (rangeValue) {
						node.RANGE_VALUES = rangeValue;
						setModal(null);
					}
				} else {
					if (rangeValue) {
						node['RANGE_VALUES'] = rangeValue;
						setModal(null);
					}
				}
				tableRef.current.scrollTop = tableRef.current.clientHeight;
			}
			if (action === CONTAINS_TYPE.DATE_CONTAINS) {
				const rangeValue = errorData(rows, newVal);
				if (node?.BY_PASS_VALIDATION_ALLOW_VALUES) {
					if (rangeValue) {
						node.BY_PASS_VALIDATION_ALLOW_VALUES = rangeValue;
						setModal(null);
					}
				} else {
					if (rangeValue) {
						node['BY_PASS_VALIDATION_ALLOW_VALUES'] = rangeValue;
						setModal(null);
					}
				}
			}
		} else if (val.VALUE === '') {
			setErrorModal(true);
			setErrorMessage(t('DIAG_NEW_DATA_VALIDATION_CONTAINS_VALUE_DESC'));
		}
		submitChange(selectedNode?.key, node, 'add');
	}

	const handleQuickAddInput = (vals) => {
	    let newRows = [...rows];
		// Get the next available ID
		let nextId = newRows.length > 0 ? Math.max(...newRows.map(row => row.ID || 0)) + 1 : 0;
		
		// Process each object in the array
		vals.forEach(val => {
			if (val.VALUE !== '') {
				if (action === CONTAINS_TYPE.CONTAINS) {
					// Add ID to the new value
					const newVal = { ...val, ID: nextId++ };
					newRows = [...newRows, newVal];
					const rangeValue = getRangeValueAsString(newRows);
					if (selectedNode?.CODE_LIST_DE_ID && rangeValue) {
						let formattedCode = `${val.VALUE}%1F${val.DESCRIPTION.replace(' ', '+')}%1E`;
						const newCodeListAdd = node.hasOwnProperty('CODELIST_ADD')
							? { ...node.CODELIST_ADD, CODES: node.CODELIST_ADD.CODES + formattedCode }
							: { CODES: formattedCode };
	
						node.CODELIST_ADD = newCodeListAdd;
					} else if (node?.RANGE_VALUES) {
						if (rangeValue) {
							node.RANGE_VALUES = rangeValue;
						}
					} else {
						if (rangeValue) {
							node['RANGE_VALUES'] = rangeValue;
						}
					}
					tableRef.current.scrollTop = tableRef.current.clientHeight;
				}
	
				if (action === CONTAINS_TYPE.DATE_CONTAINS) {
					// Add ID to the new value for DATE_CONTAINS
					const newVal = { ...val, ID: nextId++ };
					newRows = [...newRows, newVal];
					const rangeValue = getRangeValueAsString(newRows);
					if (node?.BY_PASS_VALIDATION_ALLOW_VALUES) {
						if (rangeValue) {
							node.BY_PASS_VALIDATION_ALLOW_VALUES = rangeValue;
						}
					} else {
						if (rangeValue) {
							node['BY_PASS_VALIDATION_ALLOW_VALUES'] = rangeValue;
						}
					}
				}
			} else if (val.VALUE === '') {
				setErrorModal(true);
				setErrorMessage(t('DIAG_NEW_DATA_VALIDATION_CONTAINS_VALUE_DESC'));
			}
		});
	
		setRows(newRows);
		setModal(null);
		
		submitChange(selectedNode?.key, node, 'add');
	};
	
	const handleModify = (val) => {
		if (val?.VALUE !== '') {
			const id = selectRow?.ID;
			const currentValue = selectRow?.VALUE;
			const rangeValues = rows
				?.map((val) => val.VALUE?.toLowerCase())
				.filter((range) => range?.toLowerCase() !== currentValue.toLowerCase());
			let newRangeValues = '';

			if (rangeValues.includes(val.VALUE?.toLowerCase())) {
				setErrorModal(true);
				setErrorMessage(t('BOX_VALUE_ALREADY_EXISTS'));
			} else {
				for (let i = 0; i < rows.length; i++) {
					if (rows[i].ID === id) {
						rows[i].VALUE = val.VALUE;
						rows[i].DESCRIPTION = val.DESCRIPTION;
						setSelectedValues([val.VALUE])
						setSelectRow(val);
						break;
					}
				}
				newRangeValues = getRangeValueAsString(rows);
				if (action === CONTAINS_TYPE.CONTAINS) {
					if (selectedNode?.CODE_LIST_DE_ID && newRangeValues) {
						// node['CODELIST_ADD'] = newRangeValues;
						setModal(null);
					}
					else node.RANGE_VALUES = newRangeValues;
				}
				if (action === CONTAINS_TYPE.DATE_CONTAINS) {
					node.BY_PASS_VALIDATION_ALLOW_VALUES = newRangeValues;
				}
				submitChange(selectedNode?.key, node);
				setModal(null);
				setErrorModal(false);

			}
		} else {
			setErrorModal(true);
			setErrorMessage(t('DIAG_RENAME_DATA_VALIDATION_CONTAINS_VALUE_DESC'));
		}
	}

	const deleteItems = () => {
		setDeleteModal(true);
	}

	const toggle = () => {
		setDeleteModal(false);
	}

	const handleDelete = () => {
		if (selectedValues.length !== 0) {
			const { VALUE, DESCRIPTION } = selectRow;
			const remain = rows?.filter((row) => {
				if (!selectedValues.includes(row.VALUE)) return row;
			});
			if (remain.length > 0) {
				const asStringAfterDelete = remain?.length > 0 && getRangeValueAsString(remain);
				// To update the indexes after the delete of items
				const asArrayAfterUpdatedIndex = getRangeValueAsArray(asStringAfterDelete);
				setRows(asArrayAfterUpdatedIndex);
				if (action === CONTAINS_TYPE.CONTAINS){
					if(templateHeader?.FILETYPE === 'EDI' && selectedNode?.CODE_LIST_DE_ID){
						let formattedCode = `${VALUE}%1F${DESCRIPTION.replace(' ', '+')}%1E`;
						if(node['CODELIST_ADD']['CODES']?.includes(formattedCode)) {
							let newCodes = node.CODELIST_ADD.CODES.replace(formattedCode, '');
							node.CODELIST_ADD = { CODES: newCodes };
				            submitChange(selectedNode?.key, node, 'add');
							setDeleteModal(false);
							return;
						}
                        node["CODELIST_REMOVE"] = { CODES: `${VALUE}%1E` }
						submitChange(selectedNode?.key, node);
						setDeleteModal(false);
						return;
					}
					else node.RANGE_VALUES = asStringAfterDelete;
				} 
				if (action === CONTAINS_TYPE.DATE_CONTAINS) node.BY_PASS_VALIDATION_ALLOW_VALUES = asStringAfterDelete;
			} else {
				if (action === CONTAINS_TYPE.CONTAINS) node.RANGE_VALUES = '';
				if (action === CONTAINS_TYPE.DATE_CONTAINS) node.BY_PASS_VALIDATION_ALLOW_VALUES = '';
				setRows([])
			}
			setSelectedValues([]);
			submitChange(selectedNode?.key, node);
			setDeleteModal(false);
		}
	}

	const handleErrorModal = () => {
		setErrorModal(false);
		setErrorMessage('');
	}

	const submitChange = (key, val, type) => {
		let translatedData = cloneDeep(rightTree.tree[rightTree.currentTab].translatedData);
		eachDeep(translatedData, (objValue) => {
			if (objValue?.key === key) {
				if (objValue?.RANGE_TYPE === 'CONTAINS' && action === CONTAINS_TYPE.CONTAINS) {
					if (selectedNode?.CODE_LIST_DE_ID) {
						if(type === 'add'){
							objValue['CODELIST_ADD'] = val.CODELIST_ADD; 
					    } else {
							if (objValue['CODELIST_REMOVE']) {
								objValue['CODELIST_REMOVE']['CODES'] += val.CODELIST_REMOVE.CODES; 
								} else {
								objValue['CODELIST_REMOVE'] = val.CODELIST_REMOVE; 
								}
						}
					  }
					  
					else objValue['RANGE_VALUES'] = val.RANGE_VALUES;
				}
				if (action === CONTAINS_TYPE.DATE_CONTAINS) {
					objValue['BY_PASS_VALIDATION_ALLOW_VALUES'] = val?.BY_PASS_VALIDATION_ALLOW_VALUES;
				}
				return;
			}
		});
		dispatch(treeActions.setNewRightTreeTranslatedData({
			screen: parentScreen,
			type: rightTree.currentTab,
			data: translatedData
		}));
		if (action === CONTAINS_TYPE.CONTAINS) {
			dispatch(treeActions.setRightTreeSelectedNode({
				screen: parentScreen,
				type: currentTab,
				data: { ...selectedNode, RANGE_TYPE: 'CONTAINS', [selectedNode?.CODE_LIST_DE_ID ? "CODELIST_ADD" : "RANGE_VALUES"]: selectedNode?.CODE_LIST_DE_ID ? val?.CODELIST_ADD : val.RANGE_VALUES }
			}));
		}
		if (action === CONTAINS_TYPE.DATE_CONTAINS) {
			dispatch(treeActions.setRightTreeSelectedNode({
				screen: parentScreen,
				type: currentTab,
				data: { ...selectedNode, BY_PASS_VALIDATION_ALLOW_VALUES: val.BY_PASS_VALIDATION_ALLOW_VALUES }
			}));
		}
	}

	const ediContainsValueHandler = (listID, previousValue, deletedValue) => {
		let data = [];
		const existingValues = previousValue && previousValue !== "undefined" ? getRangeValueAsArray(previousValue) || [] : [];
		const deletedValues = deletedValue && deletedValue !== "undefined" ? getRangeValueAsArray(deletedValue) || [] : [];
		const parentNode = getParentNode(selectedNode, translatedData);
		const is_parent_field_group = parentNode?.ICON_DISPLAY?.includes('FIELD_GROUP');
		const field_desc = (selectedNode?.DESCRIPTION);
		const parent_segment_id = parentNode.SEGMENT_ID;
		const parent_name = (parentNode.TYPE);
		const parent_sequence = `0${parentNode?.POSITION_NUMBER}`;
		const parent_position_number = parentNode?.POSITION_NUMBER;
		dispatch(api.endpoints.get_edi_codelist.initiate({
			account: queryParams.account,
			user: queryParams.user,
			data_element_id: listID,
			release_uid: templateHeader?.FILETYPE_VERSION,
			transaction_set_id: templateHeader?.TXN_SET_TYPE,
			is_parent_field_group,
			field_desc,
			parent_segment_id,
			parent_name,
			parent_sequence,
			parent_position_number
		},{forceRefetch:true}))
			.then(res => {
				if (res?.data?.current_state === "SUCCESS") {
					const dataArray = res?.data?.data?.map((e, i) => {
						return {
							VALUE: e?.value,
							DESCRIPTION: e?.description,
							ID: i
						}
					})?.filter(e => {
						let found = false;
						existingValues.forEach(o => {
							found = o?.VALUE === e?.VALUE;
							if (found) return;
						})
						return !found;
					})
					
					data = [...dataArray, ...existingValues];
					data = data?.filter(e => {
						return !deletedValues.some(d => d?.VALUE === e?.VALUE);
					}) || [];
					setRows(data);

				}
				if (previousValue && res?.data?.current_state !== "SUCCESS"){
					let combinedData = [...data, ...existingValues];
					combinedData = combinedData?.filter(e => {
						return !deletedValues.some(d => d?.VALUE === e?.VALUE);
					}) || [];
					setRows(combinedData);

				} 
			})
	}

	useEffect(() => {
		const selectedNodes = getSelectedNodes([selectedNode?.key], translatedData);
		setNode(selectedNodes[0]);
		if (action === CONTAINS_TYPE.CONTAINS) {
			if (templateHeader?.FILETYPE === "EDI" && selectedNode?.CODE_LIST_DE_ID) {
				ediContainsValueHandler(selectedNode?.CODE_LIST_DE_ID, selectedNode?.CODELIST_ADD, selectedNode?.CODELIST_REMOVE);
			}
			else if (selectedNode?.RANGE_VALUES) {
				const convertedValues = getRangeValueAsArray(selectedNode?.RANGE_VALUES);
				setRows(convertedValues);
			}
		}
		if (action === CONTAINS_TYPE.DATE_CONTAINS) {
			if (selectedNode?.BY_PASS_VALIDATION_ALLOW_VALUES) {
				const convertedValues = getRangeValueAsArray(selectedNode?.BY_PASS_VALIDATION_ALLOW_VALUES);
				setRows(convertedValues);
			}
		}

		return () => {
			setRows([]);
		}

	}, [selectedNode?.key]);

	return (
		<>
			<div className='control-row-wrap'>
				<Button
					variant="outlined"
					size="small"
					onClick={() => handleModals(MODALS.BTN_NEW_VALUE)}
				>
					{t('BTN_NEW_VALUE')}
				</Button>
				<Button
					variant="outlined"
					size="small"
					onClick={() => handleModals(MODALS.BTN_NEW_VALUES_QUICK_ADD)}
				>
					{t('BTN_NEW_VALUES_QUICK_ADD')}
				</Button>
				<Button
					variant="outlined"
					size="small"
					disabled={selectedValues.length !== 1}
					onClick={() => handleModals(MODALS.BTN_MODIFY_VALUE)}
				>
					{t('BTN_MODIFY_VALUE')}
				</Button>
				<Button
					variant="outlined"
					size="small"
					disabled={selectedValues.length === 0}
					onClick={() => deleteItems()}
				>
					{t('BTN_DELETE_VALUE')}
				</Button>
			</div>
			 <div className='diag-lrh-ib_table margin-top-12' style={{border:'solid 1px #e0e0e0',borderRadius:'3px',height:'360px'}} >
				<TableContainer ref={tableRef} sx={{ maxHeight: '360px' }} component={Paper}>
					<Table size='small' stickyHeader>
						<TableHead>
							<TableRow>
							<TableCell
							  style={{
							    borderRight: "1px solid #e0e0e0",
								backgroundColor: "#f1f1f1",
							   }}>
									{t('COL_VAL')}
								</TableCell>
								<TableCell style={{ backgroundColor: "#f1f1f1"}}>
									{t('DESCRIPTION')}
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{
								rows?.map((row, index) => {
									return <TableRowContainer
										lastRow={lastRowRef}
										row={row}
										key={index}
										handleMulitpleSelection={handleMulitpleSelection}
										isSelected={selectedValues.includes(row.VALUE)}
									></TableRowContainer>
								})
							}
						</TableBody>
					</Table>
				</TableContainer>
				{/* <TableContainer ref={tableRef} sx={{maxHeight: '400px'}}  component={Paper}>
                    <StyledTable>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    {t('COL_VAL')}
                                </TableCell>
                                <TableCell>
                                    {t('DESCRIPTION')}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {
                                rows?.map((row, index) => {
                                    return <TableRowContainer
                                        lastRow={lastRowRef}
                                        row={row}
                                        key={index}
                                        handleMulitpleSelection={handleMulitpleSelection}
                                        isSelected={selectedValues.includes(row.VALUE)}
                                    ></TableRowContainer>
                                })
                            }
                        </TableBody>
                    </StyledTable>
                </TableContainer> */}
			</div>
			<AddNewValue
				openModal={modal === MODALS.BTN_NEW_VALUE}
				toggle={() => setModal(null)}
				handleInput={handleInput}
				row={{ VALUE: '', DESCRIPTION: '' }}
				action=''
			></AddNewValue>
			<AddNewValue
				openModal={modal === MODALS.BTN_MODIFY_VALUE}
				toggle={() => setModal(null)}
				row={selectRow}
				handleModify={handleModify}
				action='MODIFY'
			></AddNewValue>
			{modal === MODALS.BTN_NEW_VALUES_QUICK_ADD && <QuickAddNewValues
				action={action}
				openModal={modal === MODALS.BTN_NEW_VALUES_QUICK_ADD}
				toggle={() => setModal(null)}
				row={{ VALUE: '', DESCRIPTION: '' }}
				handleQuickAddInput={handleQuickAddInput}
				keyValuePairs={rows}
			/>}
			{errorModal ? <ErrorModal
				errorModal={errorModal}
				handleErrorModal={handleErrorModal}
				errorMessage={errorMessage}></ErrorModal> : ""}
			{deleteModal ? <DeleteModal
				open={deleteModal}
				toggle={toggle}
				selectedValues={selectedValues}
				handleDelete={handleDelete}
			>
			</DeleteModal> : ""}
		</>
	)
}

const TableRowContainer = ({ row, index, selectHandler, isSelected, handleMulitpleSelection, lastRow }) => {

	const clickHandler = (e) => {
		handleMulitpleSelection(e, row);
	};

	return <TableRow
		ref={lastRow}
		key={index}
		className={isSelected ? "selected-table-row" : ""}
		sx={{
			"&:last-child td, &:last-child th": { border: 0 },
			cursor: "pointer",
			borderBottom: "1px solid #e0e0e0",
		}}
		onClick={(e) => clickHandler(e)}
	>
		<TableCell component="th" scope="row" style={{ borderRight: "1px solid #e0e0e0" }} >{row?.VALUE}</TableCell>
		<TableCell>{row?.DESCRIPTION}</TableCell>
	</TableRow>
}

const DeleteModal = ({ open, toggle, selectedValues,handleDelete }) => {
	const { t } = useTranslation();
	const deleteItems = () => {
		handleDelete();
	}
	return <>
		<Dialog open={open}>
			<DialogTitle className='title'>
				{t("BOX_CONFIRM")}
			</DialogTitle>
			<DialogContent>
				{t('BOX_ARE_YOU_SURE_YOU_WANT_TO_DEL_ITEMS')?.split('{0}').join(` ${selectedValues.length} `)}
			</DialogContent>
			<DialogActions>
				<Button
					variant="outlined"
					color="primary"
					onClick={deleteItems}
					size='small'
				>
					{t("BTN_YES")}
				</Button>
				<Button
					variant="outlined"
					color="primary"
					onClick={toggle}
					size='small'
				>
					{t("BTN_NO")}
				</Button>
			</DialogActions>
		</Dialog>
	</>
}
export default Contains